import { useEffect, useSyncExternalStore, type DependencyList } from 'react';

import type { MenuAccelerators } from '@ai-studio/shared';

import { eventToChord, formatChord, isPlainChord, toAccelerator } from './chord.js';
import { BUILTIN_PROFILES, CORE_COMMANDS } from './core-keymap.js';
import { GLOBAL, ShortcutRegistry, STORAGE_KEY, contextDepth, type Command, type CommandSpec } from './registry.js';

/**
 * Shortcut registry public API — see apps/web/README.md "Shortcuts, pies and
 * search" for the contract other editor areas plug into.
 *
 *   useCommands([{ id: 'scene.duplicate', label: 'Duplicate', context: 'scene',
 *                  keys: ['Ctrl+D'], run: duplicate }], [duplicate]);
 *   const hint = useKeyHint('scene.duplicate');   // "Ctrl+D", follows rebinding
 *   useShortcutContext('model.rig', rigOpen);      // enable a sub-context
 */

export * from './chord.js';
export { BUILTIN_PROFILES, CORE_COMMANDS } from './core-keymap.js';
export type { Command, CommandSpec, Conflict, KeymapProfile, Shadow } from './registry.js';
export { GLOBAL, contextIncludes } from './registry.js';

function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The one registry for this window (each project frame has its own; they share storage). */
export const shortcuts = new ShortcutRegistry(BUILTIN_PROFILES, browserStorage());

if (typeof window !== 'undefined') {
  // Another project tab (or the host) rebound something: pick it up.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) shortcuts.reload();
  });
}

/** Register commands for as long as the calling component is mounted. */
export function useCommands(specs: readonly CommandSpec[], deps: DependencyList): void {
  useEffect(() => shortcuts.register(specs), deps);
}

export function registerCommands(specs: CommandSpec | readonly CommandSpec[]): () => void {
  return shortcuts.register(specs);
}

export function runCommand(id: string): boolean {
  return shortcuts.run(id);
}

/** Re-render on any registry or binding change; returns the change counter. */
export function useShortcutVersion(): number {
  return useSyncExternalStore(
    (fn) => shortcuts.subscribe(fn),
    () => shortcuts.version,
    () => shortcuts.version,
  );
}

/** Display label of a command's first chord ("" when unbound). */
export function keyHint(id: string): string {
  const key = shortcuts.primaryKey(id);
  return key ? formatChord(key) : '';
}

/** `keyHint` that re-renders when bindings change. */
export function useKeyHint(id: string): string {
  useShortcutVersion();
  return keyHint(id);
}

/** "Label (Ctrl+K)" for tooltips. */
export function useTitleWithKey(label: string, id: string): string {
  const hint = useKeyHint(id);
  return hint ? `${label} (${hint})` : label;
}

/* ------------------------------- contexts ------------------------------- */

let viewContext: () => string | null = () => null;
const enabled = new Map<string, number>();

/** Workspace tells the dispatcher which view has focus (`scene`, `model`, ...). */
export function setViewContextProvider(provider: () => string | null): void {
  viewContext = provider;
}

/** Turn on a sub-context (`model.rig`) while something is open. Returns the disposer. */
export function enableContext(context: string): () => void {
  enabled.set(context, (enabled.get(context) ?? 0) + 1);
  return () => {
    const n = (enabled.get(context) ?? 1) - 1;
    if (n <= 0) enabled.delete(context);
    else enabled.set(context, n);
  };
}

export function useShortcutContext(context: string, active = true): void {
  useEffect(() => (active ? enableContext(context) : undefined), [context, active]);
}

/** Active contexts, deepest first, ending in `global`. */
export function activeContexts(): string[] {
  const view = viewContext();
  const out: string[] = [];
  if (view) {
    for (const context of enabled.keys()) if (context.startsWith(`${view}.`)) out.push(context);
    out.sort((a, b) => contextDepth(b) - contextDepth(a));
    out.push(view);
  }
  out.push(GLOBAL);
  return out;
}

/* ------------------------------- dispatch ------------------------------- */

let suspended = 0;

/** Stop dispatching (while the Shortcut Manager records a chord). Returns the resume function. */
export function suspendShortcuts(): () => void {
  suspended++;
  let done = false;
  return () => {
    if (!done) suspended--;
    done = true;
  };
}

/** Text entry has focus (works for elements of torn-off panel windows too). */
export function isTextTarget(target: EventTarget | null): boolean {
  const el = target as (Element & { isContentEditable?: boolean }) | null;
  if (!el || el.nodeType !== 1) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'color', 'range'].includes(type);
  }
  return Boolean(el.closest('.monaco-editor, .xterm'));
}

/** Keys pressed into the running game go to the game, not the editor. */
function isGameTarget(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (el?.ownerDocument?.pointerLockElement) return true;
  return Boolean(el && el.nodeType === 1 && el.tagName === 'CANVAS' && el.closest('[data-dock-panel="play"]'));
}

export type ConflictHandler = (chord: string, commands: Command[]) => void;
let conflictHandler: ConflictHandler | null = null;

/**
 * What to do when one chord is bound to several commands in the same active
 * context (Unity's "Shortcut Conflict" prompt): the Workspace shows a chooser.
 * Without a handler the first command runs.
 */
export function setConflictHandler(handler: ConflictHandler | null): void {
  conflictHandler = handler;
}

/** Commands a keydown would run, most specific first, skipping the ones that can't fire here. */
function runnable(event: KeyboardEvent, chord: string, contexts: readonly string[]): Command[] | 'external' {
  const typing = isTextTarget(event.target);
  const game = isGameTarget(event.target);
  const out: Command[] = [];
  for (const command of shortcuts.match(chord, contexts)) {
    // A view still handles this chord itself: let the event through untouched.
    if (command.external) {
      if (out.length === 0) return 'external';
      break;
    }
    if (typing && !command.whileTyping) continue;
    if (game && isPlainChord(chord)) continue;
    if (command.enabled && !command.enabled()) continue;
    out.push(command);
  }
  return out;
}

/**
 * Route one keydown through the registry. Returns true when a command took it
 * (the event is then consumed so a view's own legacy handler never doubles it).
 */
export function dispatchShortcut(event: KeyboardEvent, contexts = activeContexts()): boolean {
  if (suspended > 0) return false;
  const chord = eventToChord(event);
  if (!chord) return false;
  const candidates = runnable(event, chord, contexts);
  if (candidates === 'external' || candidates.length === 0) return false;
  event.preventDefault();
  event.stopImmediatePropagation();
  const [first] = candidates as [Command, ...Command[]];
  const tied = candidates.filter((c) => c.context === first.context);
  if (tied.length > 1 && conflictHandler) {
    if (!event.repeat) conflictHandler(chord, tied);
    return true;
  }
  if (!event.repeat || first.repeat) shortcuts.run(first.id);
  return true;
}

/* ------------------------------- recents -------------------------------- */

const RECENT_KEY = 'ai-studio:recent-commands';
/** Commands that open, move between or repeat things; not worth repeating or listing as recent. */
const NOT_RECENT = /^(palette\.|search\.|favorites\.|pie\.|edit\.(undo|redo|repeat)|view\.tab\.)/;

function readRecent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string').slice(0, 20) : [];
  } catch {
    return [];
  }
}

let recent: string[] = typeof localStorage === 'undefined' ? [] : readRecent();

/** Recently run commands, newest first (Blender's F3 lists these first). */
export function recentCommands(): string[] {
  return recent.filter((id) => shortcuts.get(id));
}

shortcuts.onRun((command) => {
  if (NOT_RECENT.test(command.id) || command.hidden) return;
  recent = [command.id, ...recent.filter((id) => id !== command.id)].slice(0, 20);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
  } catch {
    // not remembered
  }
});

/** Listen on a window (capture phase, ahead of view handlers). Returns the disposer. */
export function installShortcutDispatcher(target: Window): () => void {
  const onKey = (event: KeyboardEvent) => {
    dispatchShortcut(event);
  };
  target.addEventListener('keydown', onKey, true);
  return () => target.removeEventListener('keydown', onKey, true);
}

/**
 * Current bindings as Electron accelerators, for the native menus. The tab
 * host never mounts a Workspace, so the core command table is registered here
 * (metadata only) to resolve defaults; user bindings come from shared storage.
 */
let coreKnown = false;
export function menuAccelerators(ids?: readonly string[]): MenuAccelerators {
  if (!coreKnown) {
    coreKnown = true;
    for (const spec of CORE_COMMANDS) if (!shortcuts.get(spec.id)) shortcuts.register(spec);
  }
  shortcuts.reload();
  const out: Record<string, string> = {};
  for (const id of ids ?? CORE_COMMANDS.map((c) => c.id)) {
    const chord = shortcuts.primaryKey(id);
    const accelerator = chord ? toAccelerator(chord) : null;
    if (accelerator) out[id] = accelerator;
  }
  return out;
}
